import React from "react";

/**
 * Renders `text` with every case-insensitive match of `query` wrapped in a
 * yellow `<mark>` tag. Handles multi-token queries — each whitespace-separated
 * token is highlighted independently so `"24 Gopi"` lights up both "24" and
 * "Gopi" wherever they appear.
 *
 * Safe against user input: `query` is regex-escaped.
 */
export default function HighlightMatch({ text, query, className }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!str || !q) return <span className={className}>{str}</span>;

  const tokens = q.split(/\s+/).filter((t) => t.length > 0);
  if (!tokens.length) return <span className={className}>{str}</span>;

  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = str.split(re);
  const lowerTokens = new Set(tokens.map((t) => t.toLowerCase()));

  return (
    <span className={className}>
      {parts.map((part, i) =>
        lowerTokens.has(String(part).toLowerCase()) ? (
          <mark key={i} className="bg-yellow-200 text-gray-900 rounded-sm px-0.5">
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </span>
  );
}
